import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  EventqDialogPopover,
  EventqDialogPopoverPlacement,
} from '../dialog-popover/dialog-popover';
import { EventqEventOutcomeList } from '../event-outcome-list/event-outcome-list';
import type {
  EventOutcomeConfirm,
  EventOutcomeGroup,
  EventOutcomePriority,
} from '../event-outcome/event-outcome.types';
import { EVENT_OUTCOME_PRIORITY_ICONS } from '../event-outcome/event-outcome.types';
import { EventqPopoverSearch } from '../popover-search/popover-search';
import {
  EventqPriorityFilterOption,
  EventqPriorityFilterRow,
} from '../priority-filter-row/priority-filter-row';

export type { EventOutcomeConfirm, EventOutcomeGroup, EventOutcomePriority };

const DEFAULT_RAISE_PRIORITY_OPTIONS: EventqPriorityFilterOption[] = [
  { id: 'all', label: 'ALL' },
  { id: 'p1', label: 'P1', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p1 },
  { id: 'p2', label: 'P2', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p2 },
  { id: 'p3', label: 'P3', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p3 },
  { id: 'p4', label: 'P4', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p4 },
  { id: 'p5', label: 'P5', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p5 },
];

@Component({
  selector: 'eventq-raise-event-popover',
  imports: [EventqDialogPopover, EventqPopoverSearch, EventqPriorityFilterRow, EventqEventOutcomeList],
  templateUrl: './raise-event-popover.html',
  styleUrl: './raise-event-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqRaiseEventPopover {
  readonly open = input(false);
  readonly groups = input.required<EventOutcomeGroup[]>();
  readonly searchQuery = input('');
  readonly selectedPriorities = input<EventOutcomePriority[]>([]);
  readonly selectedOutcomeId = input<string | null>(null);
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement | 'auto'>('auto');
  readonly panelWidth = input<string | null>(null);
  readonly priorityOptions = input(DEFAULT_RAISE_PRIORITY_OPTIONS);
  readonly cancelLabel = input('Cancel');
  readonly confirmLabel = input('Submit and Raise Event');

  readonly closed = output<void>();
  readonly confirm = output<EventOutcomeConfirm>();
  readonly searchQueryChange = output<string>();
  readonly selectedPrioritiesChange = output<EventOutcomePriority[]>();
  readonly selectedOutcomeIdChange = output<string>();

  readonly resolvedPlacement = computed((): EventqDialogPopoverPlacement => {
    const placement = this.placement();
    if (placement !== 'auto') {
      return placement;
    }

    return this.anchor() ? 'anchor-top-end' : 'center';
  });

  readonly confirmDisabled = computed(() => !this.selectedOutcomeId());

  onConfirm(): void {
    const outcomeId = this.selectedOutcomeId();
    if (!outcomeId) {
      return;
    }

    this.confirm.emit({ outcomeId });
  }
}
